// Hook orchestration for TaskListBase — keeps the list component under max-lines.
// @ts-check
import { useMemo, useCallback } from 'react';
import useNewTask from '@/features/tasks/hooks/mutations/use-task-new-state';
import { useTaskListActions } from '@/features/tasks/hooks/list/use-list-actions';
import { getNewTaskInsertIndex } from '@/features/tasks/utils/structure/taskHierarchy';
import { TASK_LIST_BASE_DEFAULTS } from './taskListBaseDefaults';
import { useTaskListFocusHandlers } from './useTaskListFocusHandlers';
import { useTaskListFocusClaim } from './useTaskListFocusClaim';
import { useTaskListClickHandlers } from './useTaskListClickHandlers';
import { buildTaskListBaseContent } from './taskListBaseContent';
import { useTaskListUiContextValue } from './useTaskListUiContextValue';
import { buildTaskListBaseControllerResult } from './useTaskListBaseController.result';
import { useTaskListBaseSharedState } from './useTaskListBaseSharedState';
import { useTaskListBaseControllerKeyboard } from './useTaskListBaseControllerKeyboard';

export function useTaskListBaseController(props) {
  const fullProps = { ...TASK_LIST_BASE_DEFAULTS, ...props };
  const {
    id,
    isSortable,
    tasks,
    editingTaskId,
    onStartEditing,
    onStopEditing,
    selectedTaskIds,
    onSelectionChange,
    selectNewTask,
    indentationType,
    plannedParentIds,
    waitingForParentIds,
    view,
    showBreadcrumbs,
    onBeforeToggleComplete,
    topActionableTaskId,
    showPlayButtonAlways,
    dragEnabled,
    disableStructuralNav,
    listTabIndex,
    itemTabIndex,
    disableRowNavigate,
    listId,
    focusOwnerId,
    addSubtaskOnParentsOnly,
    menuSurface,
    dragType,
    planMode,
    onboardingHighlightTaskId,
  } = fullProps;

  const shared = useTaskListBaseSharedState({
    tasks,
    selectedTaskIds,
    onSelectionChange,
  });
  const {
    allTasks,
    tasksMap,
    user,
    duplicateOrderKeyTaskIds,
    rebalanceOrderKeys,
    newTaskSessionRef,
    cancelNewTaskInputRef,
    taskListRef,
    selectSingleTask,
    toggleTaskInSelection,
    selectRangeTasks,
    focusContainer,
    deselectAllTasks,
    selectedIndex,
    isFocused,
    setIsFocused,
    isBulkSelectMode,
    setIsBulkSelectMode,
    addTask,
    updateTask,
  } = shared;

  useTaskListFocusClaim({
    selectedIndex,
    isFocused,
    focusContainer,
    listId,
    focusOwnerId,
  });

  const listActions = useTaskListActions({
    allTasks,
    user,
    addTask,
    updateTask,
    selectNewTask,
    editingTaskId,
    onStopEditing,
    deselectAllTasks,
    selectSingleTask,
    taskListRef,
    newTaskSessionRef,
    cancelNewTaskInputRef,
  });

  const newTask = useNewTask({
    onAddTask: listActions.handleAddTask,
    sessionRef: newTaskSessionRef,
  });
  cancelNewTaskInputRef.current = newTask.cancel;

  const { handleTaskClick, handleLongPressSelect: handleBulkLongPress } =
    useTaskListClickHandlers({
      tasks,
      selectedIndex,
      newTaskPosition: newTask.newTaskPosition,
      cancel: newTask.cancel,
      focusContainer,
      selectSingleTask,
      toggleTaskInSelection,
      selectRangeTasks,
      setIsBulkSelectMode,
    });

  const { handleCancelNewTaskInput } = useTaskListBaseControllerKeyboard({
    tasks,
    shared,
    selectSingleTask,
    deselectAllTasks,
    newTask,
    onStartEditing,
    editingTaskId,
    view,
    planMode,
    disableStructuralNav,
    disableRowNavigate,
    selectedTaskIds,
    handleStartAddingSubtask: listActions.handleStartAddingSubtask,
    addingSubtaskTo: listActions.addingSubtaskTo,
  });

  const handleRebalance = useCallback(
    (taskId) => {
      if (rebalanceOrderKeys) rebalanceOrderKeys(taskId, allTasks);
    },
    [rebalanceOrderKeys, allTasks],
  );
  const { handleFocus, handleBlur } = useTaskListFocusHandlers(
    taskListRef,
    setIsFocused,
  );
  const isMultiSelectActive = isBulkSelectMode && selectedTaskIds.length > 0;
  const newTaskInputIndex = getNewTaskInsertIndex(
    tasks,
    newTask.newTaskPosition,
  );
  const items = useMemo(
    () => tasks.map((t) => (listId ? `${listId}:${t.id}` : t.id)),
    [tasks, listId],
  );
  const uiContextValue = useTaskListUiContextValue({
    view,
    indentationType,
    showBreadcrumbs,
    plannedParentIds,
    waitingForParentIds,
    isFocused,
    selectedTaskIds,
    isMultiSelectActive,
    duplicateOrderKeyTaskIds,
    handleRebalance,
    handleTaskClick,
    handleSelectSingle: selectSingleTask,
    handleBulkLongPress,
    handleStartAddingSubtask: listActions.handleStartAddingSubtask,
    onBeforeToggleComplete,
    dragEnabled,
    listId,
    focusOwnerId,
    addSubtaskOnParentsOnly,
    menuSurface,
    dragType,
    disableRowNavigate,
    itemTabIndex,
    onboardingHighlightTaskId,
    topActionableTaskId,
  });

  return buildTaskListBaseControllerResult({
    id,
    isSortable,
    dragEnabled,
    items,
    uiContextValue,
    taskListRef,
    tasks,
    tasksMap,
    showBreadcrumbs,
    view,
    topActionableTaskId,
    showPlayButtonAlways,
    handleFocus,
    handleBlur,
    indentationType,
    newTaskInputIndex,
    newTask,
    handleCancelNewTaskInput,
    editingTaskId,
    listActions,
    onBeforeToggleComplete,
    listTabIndex,
    itemTabIndex,
    buildTaskListBaseContent,
  });
}
